// Reads statistical results as papers print them ("t(39) = −2.64, p = .012, d = −0.42", or a whole
// results paragraph) into structured pieces for the Result Decoder (lib/result_decode.js).
// Pure functions, no DOM, so it runs in Node too: spec/javascript/result_check.mjs checks it.
// It never throws on odd input: anything it can't place is returned in `unread`.

// ---------- Normalizing ----------

const DASHES = /[‐‑‒–—―−﹘﹣－]/g;
const RANGE_DASH = /(\d)\s*[–—]\s*(?=-?\s*[.\d])/g; // 0.20–0.50 is a range, not a minus

// Replaces runs of text with canonical tokens (see TOKENS below) so the item patterns stay simple.
// Each rule is [pattern, replacement]; patterns run in order on NFKC-normalized text.
const CANON = [
	// Markdown or Word italic markers left over from copy-paste.
	[/[*_]{1,2}(?=[A-Za-zα-ωΑ-Ω])|(?<=[A-Za-zα-ωΑ-Ω0-9)\]])[*]{1,2}/g, ""],
	// Superscript/caret 2 written out: χ^2, R^{2}, η^2.
	[/\^\s*\{?\s*2\s*\}?/g, "2"],
	// PDFs often store χ in a symbol font, so a copied χ² can arrive as "c2", "v2", a private-use glyph,
	// Latin ꭓ, Cyrillic х or a replacement character. Read those as χ only right before "2(df)" or "2/df",
	// where nothing else fits.
	[/(?<![A-Za-z0-9])[cv\uF063\uAB53\u03F0\u0445\u00D7\uFFFD]\s*2\s*(?=[([]\s*\d|\/\s*df(?![A-Za-z]))/g, " χ2"],
	// The normed (relative) chi-square, χ²/df, before χ² itself is read: "χ2/df", "χ 2 /df" (a PDF's superscript
	// on its own line), "chi2/df", "CMIN/DF", "relative chi-square", "normed χ²".
	[/(?<![A-Za-z])(?:(?:normed|relative)\s+)?(?:χ|Χ|chi|x)\s*-?\s*(?:2|squared?)\s*\/\s*df(?![A-Za-z])|(?<![A-Za-z])CMIN\s*\/\s*DF(?![A-Za-z])|(?<![A-Za-z])(?:normed|relative)\s+(?:χ|chi)\s*-?\s*(?:2|squared?)(?:\s*\(\s*(?:χ|chi)?\s*-?\s*2?\s*\/\s*df\s*\))?/gi, " CHI2DF "],
	// Chi-square, likelihood-ratio G².
	[/(?<![A-Za-z])(?:χ|Χ|chi|x)\s*-?\s*(?:2|squared?)(?:\s*test)?(?=\s*(?:[([]|[=<>≤≥]))/gi, " chi2"],
	[/(?<![A-Za-z])(?:χ|Χ)(?=\s*[([])/g, " chi2"],
	[/(?<![A-Za-z])G2(?=\s*[([])/g, " G2"],
	// Eta squared and its kinds. "partial η²", "ηp²", "η2p", "η_p^2", "eta-squared", generalized η²G.
	[/(?<![A-Za-z])partial\s+(?:η|eta)\s*-?\s*(?:2|squared)\s*(?:_?\s*\{?p\}?)?/gi, " ETAP2 "],
	[/(?<![A-Za-z])(?:η|eta)\s*_?\s*\{?\s*([pPgG])\s*\}?\s*2(?![\d.])/g, (m, k) => (k.toLowerCase() === "p" ? " ETAP2 " : " ETAG2 ")],
	[/(?<![A-Za-z])(?:η|eta)\s*-?\s*(?:2|squared)\s*_?\s*\{?\s*([pPgG])\s*\}?(?![A-Za-z])/g, (m, k) => (k.toLowerCase() === "p" ? " ETAP2 " : " ETAG2 ")],
	[/(?<![A-Za-z])(?:η|eta)\s*-?\s*(?:2|squared)/gi, " ETA2 "],
	[/(?<![A-Za-z])partial\s+(?:ω|omega)\s*-?\s*(?:2|squared)|(?<![A-Za-z])(?:ω|omega)\s*_?\s*\{?p\}?\s*2|(?<![A-Za-z])(?:ω|omega)\s*-?\s*(?:2|squared)\s*_?\s*\{?p\}?(?![A-Za-z])/gi, " OMEGAP2 "],
	[/(?<![A-Za-z])(?:ω|omega)\s*-?\s*(?:2|squared)/gi, " OMEGA2 "],
	[/(?<![A-Za-z])(?:ε|epsilon)\s*-?\s*(?:2|squared)/gi, " EPS2 "],
	// Correlations and other effect sizes with names attached.
	[/(?<![A-Za-z])(?:Cram[eé]r'?s?\s*)V(?![A-Za-z])/gi, " CRAMERV "],
	[/(?<![A-Za-z])(?:φ|phi)(?![A-Za-z])/gi, " PHI "],
	[/(?<![A-Za-z])(?:Cohen'?s?\s*)?d\s*_?\s*\{?z\}?(?![A-Za-z])/g, " DZ "],
	[/(?<![A-Za-z])Cohen'?s?\s+d(?![A-Za-z])/gi, " d"],
	[/(?<![A-Za-z])Hedges'?s?\s+g(?![A-Za-z])/gi, " g"],
	[/(?<![A-Za-z])Cohen'?s?\s+f\s*2(?![A-Za-z])|(?<![A-Za-z])f2(?=\s*[=<>≤≥])/gi, " F2ES "],
	[/(?<![A-Za-z])Cohen'?s?\s+w(?![A-Za-z])/gi, " w"],
	[/(?<![A-Za-z])(?:Spearman'?s?\s*)?(?:ρ|rho)(?![A-Za-z])|(?<![A-Za-z])r\s*_?\s*\{?s\}?(?=\s*[([=<>≤≥])/gi, " RS"],
	[/(?<![A-Za-z])(?:Kendall'?s?\s*)?(?:τ|tau)\s*(?:_?\s*\{?b\}?)?(?![A-Za-z])/gi, " TAU"],
	[/(?<![A-Za-z])(?:Δ|delta\s*)R\s*2(?![\d.])/gi, " DR2 "],
	[/(?<![A-Za-z])(?:adj(?:usted)?\.?\s*)R\s*2(?![\d.])|(?<![A-Za-z])R\s*2\s*_?\s*\{?adj\}?(?![A-Za-z])/gi, " ADJR2 "],
	[/(?<![A-Za-z])R\s*2(?![\d.])|(?<![A-Za-z])R-squared/g, " R2 "],
	[/(?<![A-Za-z])BF\s*_?\s*\{?(10|01)\}?/g, (m, k) => ` BF${k} `],
	[/(?<![A-Za-z])odds\s+ratio(?![A-Za-z])/gi, " OR"],
	[/(?<![A-Za-z])(?:Exp\s*\(\s*B\s*\)|e\s*\^?\s*B)(?![A-Za-z])/g, " OR"],
	// p-value wording.
	[/(?<![A-Za-z])p\s*-?\s*values?(?=\s*[=<>≤≥])|(?<![A-Za-z])ps(?=\s*[=<>≤≥])|(?<![A-Za-z])p'?s(?=\s*[=<>≤≥])/gi, " p"],
	[/(?<![A-Za-z])p\s*_?\s*\{?(?:adj|corr|Holm|Bonf|bonferroni|FDR|HB|Tukey)[A-Za-z]*\.?\}?(?=\s*[=<>≤≥])/gi, " PADJ"],
	[/<=/g, "≤"], [/>=/g, "≥"]
];

// Turns pasted text into one line of canonical text. Exported for the checks.
export function normalize(text) {
	let s = String(text ?? "").normalize("NFKC");
	s = s.replace(/[  -​  　\t\r\n]+/g, " ");
	s = s.replace(/[‘’ʼ`´]/g, "'").replace(/[“”]/g, '"');
	s = s.replace(RANGE_DASH, "$1 to ").replace(DASHES, "-");
	for (const [re, rep] of CANON) s = s.replace(re, rep);
	return s.replace(/ {2,}/g, " ").trim();
}

// ---------- Numbers ----------

const NUM = String.raw`(?=-?\s?\.?\d)-?\s?(?:\d{1,3}(?:,\d{3})+(?![\d])|\d+)?(?:\.\d+)?(?:\s?(?:e|E)\s?-?\d+|\s?[x×]\s?10\s?\^?\s?-?\d+)?`;

// Reads "−2.64", ".012", "1,203", "4.2e-5", "4.2 × 10^-5". Returns { value, decimals, text } or null.
export function readNumber(raw) {
	if (raw == null) return null;
	let s = String(raw).replace(/\s+/g, "");
	if (!/\d/.test(s)) return null;
	let exp = 0;
	const sci = s.match(/^(.*?)(?:[eE]|[x×]10\^?)(-?\d+)$/);
	if (sci) { s = sci[1]; exp = parseInt(sci[2], 10); }
	if (/^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) s = s.replace(/,/g, "");
	if (!/^-?(\d+\.?\d*|\.\d+)$/.test(s)) return null;
	const mantissa = parseFloat(s);
	if (!Number.isFinite(mantissa)) return null;
	const dec = (s.split(".")[1] || "").length;
	const value = exp ? mantissa * Math.pow(10, exp) : mantissa;
	return { value, decimals: Math.max(0, dec - exp), text: String(raw).trim(), leadingZero: /^-?0\./.test(s) };
}

const COMP = { "=": "=", "<": "<", ">": ">", "≤": "<", "≥": ">" };

// ---------- Items ----------

// Each pattern finds one kind of piece. Order matters only for overlapping matches (earlier wins).
const DF_OPEN = String.raw`\s?[(\[]\s?(?:df\s?=\s?)?`;
const DF_CLOSE = String.raw`\s?[)\]]`;
const SAMPLE = String.raw`(?:\s?[,;]\s?(?:[Nn]|total\s?N)\s?=\s?(\d{1,3}(?:,\d{3})+|\d+))?`;
const CMP = String.raw`\s?([=<>≤≥])\s?`;

const PATTERNS = [
	// F(df1, df2) = value. df1 may print as I or l when it's 1.
	{ kind: "stat", type: "F", re: new RegExp(String.raw`(?<![A-Za-z])(F(?:s|\s?_?\{?change\}?)?)${DF_OPEN}(\d+(?:\.\d+)?|[Il])\s?[,;]\s?(\d+(?:\.\d+)?)${DF_CLOSE}${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: m[1], df1: m[2] === "I" || m[2] === "l" ? 1 : parseFloat(m[2]), df2: parseFloat(m[3]), dfText: `${m[2]}, ${m[3]}`, comp: m[4], num: m[5] }) },
	// χ²(df, N = n) = value, and G²(df).
	{ kind: "stat", type: "chi2", re: new RegExp(String.raw`(?<![A-Za-z])(chi2|G2)${DF_OPEN}(\d+(?:\.\d+)?)${SAMPLE}${DF_CLOSE}${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: m[1], df1: parseFloat(m[2]), N: m[3], dfText: m[2], comp: m[4], num: m[5] }) },
	// χ²(N = n, df = k) = value: sample size first.
	{ kind: "stat", type: "chi2", re: new RegExp(String.raw`(?<![A-Za-z])(chi2|G2)\s?[(\[]\s?[Nn]\s?=\s?(\d{1,3}(?:,\d{3})+|\d+)\s?[,;]\s?(?:df\s?=\s?)?(\d+(?:\.\d+)?)${DF_CLOSE}${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: m[1], df1: parseFloat(m[3]), N: m[2], dfText: m[3], comp: m[4], num: m[5] }) },
	// χ² = value without df (unreadable for p, but recognized).
	{ kind: "stat", type: "chi2", re: new RegExp(String.raw`(?<![A-Za-z])(chi2|G2)${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: m[1], comp: m[2], num: m[3] }) },
	// t(df), r(df), H(df), Q(df), Welch's t.
	{ kind: "stat", type: "t", re: new RegExp(String.raw`(?<![A-Za-z])((?:Welch'?s?\s)?ts?)${DF_OPEN}(\d+(?:\.\d+)?)${DF_CLOSE}${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: m[1], df2: parseFloat(m[2]), dfText: m[2], comp: m[3], num: m[4], welch: /welch/i.test(m[1]) }) },
	{ kind: "stat", type: "r", re: new RegExp(String.raw`(?<![A-Za-z])(r|RS)${DF_OPEN}(\d+(?:\.\d+)?)${DF_CLOSE}${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: m[1], df2: parseFloat(m[2]), dfText: m[2], comp: m[3], num: m[4], spearman: m[1] === "RS" }) },
	{ kind: "stat", type: "r", re: new RegExp(String.raw`(?<![A-Za-z])(r|RS)\s?[(\[]\s?[Nn]\s?=\s?(\d{1,3}(?:,\d{3})+|\d+)${DF_CLOSE}${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: m[1], N: m[2], comp: m[3], num: m[4], spearman: m[1] === "RS" }) },
	{ kind: "stat", type: "H", re: new RegExp(String.raw`(?<![A-Za-z])(H)${DF_OPEN}(\d+)${SAMPLE}${DF_CLOSE}${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: m[1], df1: parseFloat(m[2]), N: m[3], dfText: m[2], comp: m[4], num: m[5] }) },
	{ kind: "stat", type: "Q", re: new RegExp(String.raw`(?<![A-Za-z])(Q\s?-?\s?(?:within|between|w|b|W|B)?)${DF_OPEN}(\d+)${SAMPLE}${DF_CLOSE}${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: m[1].replace(/\s|-/g, ""), df1: parseFloat(m[2]), N: m[3], dfText: m[2], comp: m[4], num: m[5] }) },
	// t or F with no df: recognized, but p can't be recomputed.
	{ kind: "stat", type: "t", re: new RegExp(String.raw`(?<![A-Za-z'])(t)${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: "t", comp: m[2], num: m[3] }) },
	{ kind: "stat", type: "F", re: new RegExp(String.raw`(?<![A-Za-z'])(F)${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: "F", comp: m[2], num: m[3] }) },
	// Statistics without df.
	{ kind: "stat", type: "z", re: new RegExp(String.raw`(?<![A-Za-z'])([zZ])${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: "z", comp: m[2], num: m[3] }) },
	{ kind: "stat", type: "U", re: new RegExp(String.raw`(?<![A-Za-z'])(U)${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: "U", comp: m[2], num: m[3] }) },
	{ kind: "stat", type: "W", re: new RegExp(String.raw`(?<![A-Za-z'])(W|V|T\s?\+?|T)${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: m[1].replace(/\s/g, ""), comp: m[2], num: m[3] }) },
	{ kind: "stat", type: "H", re: new RegExp(String.raw`(?<![A-Za-z'])(H)${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: "H", comp: m[2], num: m[3] }) },
	// r, ρ and OR without df: an effect size, or a result of their own (decided when grouping).
	{ kind: "stat", type: "r", loose: true, re: new RegExp(String.raw`(?<![A-Za-z'])(r|RS)${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: m[1], comp: m[2], num: m[3], spearman: m[1] === "RS" }) },
	{ kind: "stat", type: "OR", loose: true, re: new RegExp(String.raw`(?<![A-Za-z])(OR|aOR|AOR|RRR|IRR)${CMP}(${NUM})`, "g"),
		read: (m) => ({ symbol: m[1], comp: m[2], num: m[3] }) },
	// Model-fit indices, with an interval right after (RMSEA's is usually 90%): "RMSEA = .05, 90% CI [.03, .07]",
	// "RMSEA = .05 (.03, .07)".
	{ kind: "fit", re: new RegExp(String.raw`(?<![A-Za-z'])(CHI2DF|CFI|TLI|NNFI|RMSEA|SRMR|GFI|AGFI|AIC|BIC)${CMP}(${NUM})(?:\s?[,;]?\s?(?:(\d{2})\s?%\s?(?:CI|C\.I\.|confidence interval)\s?[:=]?\s?)?[\[(]\s?(${NUM})\s?(?:,|;|to)\s?(${NUM})\s?[\])])?`, "g"),
		read: (m) => ({ label: m[1], comp: m[2], num: m[3], level: m[4], lo: m[5], hi: m[6] }) },
	// p-values and "ns".
	{ kind: "p", re: new RegExp(String.raw`(?<![A-Za-z])(p|P|PADJ)${CMP}(${NUM})`, "g"),
		read: (m) => ({ adjusted: m[1] === "PADJ", comp: m[2], num: m[3] }) },
	{ kind: "p", re: /(?<![A-Za-z])n\.?\s?s\.?(?![A-Za-z])/g, read: () => ({ comp: "ns" }) },
	// Confidence intervals: "95% CI [a, b]", "95% CI (a, b)", "95% CI = a to b", "CI: a, b".
	{ kind: "ci", re: new RegExp(String.raw`(?:(\d{2}(?:\.\d+)?)\s?%\s?)?(?:CI|C\.I\.|confidence interval)s?\s?(?:(\d{2})\s?%\s?)?\s?[:=]?\s?(?:[\[(]\s?(${NUM})\s?(?:,|;|to)\s?(${NUM})\s?[\])]|(${NUM})\s?(?:,|;|to)\s?(${NUM})(?![\d.]))`, "g"),
		read: (m) => ({ level: parseFloat(m[1] || m[2] || "95"), lo: m[3] ?? m[5], hi: m[4] ?? m[6] }) },
	// A bare bracketed interval after an estimate, "d = 0.42 [0.10, 0.74]": APA's form once "95% CI" has been named.
	{ kind: "ci", re: new RegExp(String.raw`\[\s?(${NUM})\s?[,;]\s?(${NUM})\s?\]`, "g"),
		read: (m) => ({ level: 95, assumed: true, lo: m[1], hi: m[2] }) },
	// Effect sizes.
	{ kind: "effect", re: new RegExp(String.raw`(?<![A-Za-z'])(ETAP2|ETAG2|ETA2|OMEGAP2|OMEGA2|EPS2|CRAMERV|PHI|DZ|F2ES|DR2|ADJR2|R2|TAU|d|g|w)${CMP}(${NUM})`, "g"),
		read: (m) => ({ effect: m[1], comp: m[2], num: m[3] }) },
	// Everything else a results paragraph carries, recognized so it isn't reported as unreadable.
	{ kind: "other", re: new RegExp(String.raw`(?<![A-Za-z'])(M|SD|SE|SEM|Mdn|IQR|n|N|b|B|β|beta|BF10|BF01|k|df|α|alpha|ω|κ|kappa|ICC|AUC|ab|IE)\s?(?:_?\s?\{?[A-Za-z0-9]{1,6}\}?)?${CMP}(${NUM})`, "g"),
		read: (m) => ({ label: m[1], comp: m[2], num: m[3] }) },
	{ kind: "other", re: new RegExp(String.raw`(?<![A-Za-z'])(M|SD)\s?[(\[]\s?(?:SD|SE)?\s?=?\s?(${NUM})\s?[)\]]`, "g"),
		read: (m) => ({ label: "M", comp: "=", num: m[2] }) }
];

// Labels the read-back uses for each canonical effect token, with the glossary term it belongs to.
export const EFFECTS = {
	ETAP2: { key: "eta2p", symbol: "η²p", term: "partial-eta-squared", name: "partial eta squared" },
	ETAG2: { key: "eta2g", symbol: "η²G", term: "generalized-eta-squared", name: "generalized eta squared" },
	ETA2: { key: "eta2", symbol: "η²", term: "eta-squared", name: "eta squared" },
	OMEGAP2: { key: "omega2p", symbol: "ω²p", term: "omega-squared", name: "partial omega squared" },
	OMEGA2: { key: "omega2", symbol: "ω²", term: "omega-squared", name: "omega squared" },
	EPS2: { key: "epsilon2", symbol: "ε²", term: "epsilon-squared", name: "epsilon squared" },
	CRAMERV: { key: "V", symbol: "V", term: "cramers-v", name: "Cramér's V" },
	PHI: { key: "phi", symbol: "φ", term: "phi-coefficient", name: "phi" },
	DZ: { key: "dz", symbol: "dz", term: "cohens-d", name: "Cohen's dz" },
	F2ES: { key: "f2", symbol: "f²", term: "cohens-f-squared", name: "Cohen's f²" },
	DR2: { key: "dR2", symbol: "ΔR²", term: "r-squared", name: "change in R²" },
	ADJR2: { key: "adjR2", symbol: "R²adj", term: "adjusted-r-squared", name: "adjusted R²" },
	R2: { key: "R2", symbol: "R²", term: "r-squared", name: "R²" },
	TAU: { key: "tau", symbol: "τ", term: null, name: "Kendall's tau" },
	d: { key: "d", symbol: "d", term: "cohens-d", name: "Cohen's d" },
	g: { key: "g", symbol: "g", term: "cohens-d", name: "Hedges' g" },
	w: { key: "w", symbol: "w", term: "cohens-w", name: "Cohen's w" },
	r: { key: "r", symbol: "r", term: "pearson-r", name: "r" },
	RS: { key: "rs", symbol: "rₛ", term: "spearman-rho", name: "Spearman's rho" },
	KENDALLW: { key: "kendallW", symbol: "W", term: "kendalls-w", name: "Kendall's W" },
	OR: { key: "OR", symbol: "OR", term: "odds-ratio", name: "odds ratio" }
};

const OTHER_LABELS = { M: "mean", SD: "standard deviation", SE: "standard error", SEM: "standard error", Mdn: "median", IQR: "interquartile range",
	n: "group or sample size", N: "total sample size", b: "unstandardized coefficient", B: "unstandardized coefficient", "β": "standardized coefficient",
	beta: "standardized coefficient", "α": "alpha (a significance level, or Cronbach's alpha)", alpha: "alpha (a significance level, or Cronbach's alpha)",
	"ω": "McDonald's omega", "κ": "kappa", kappa: "kappa", ICC: "intraclass correlation", AUC: "area under the curve", RMSEA: "model fit (RMSEA)",
	CFI: "model fit (CFI)", TLI: "model fit (TLI)", SRMR: "model fit (SRMR)", AIC: "information criterion", BIC: "information criterion",
	ab: "indirect effect", IE: "indirect effect", BF10: "Bayes factor for the effect", BF01: "Bayes factor for no effect", k: "number of groups or studies", df: "degrees of freedom" };

function collectItems(s) {
	const items = [];
	for (const pat of PATTERNS) {
		pat.re.lastIndex = 0;
		let m;
		while ((m = pat.re.exec(s))) {
			if (m[0].length === 0) { pat.re.lastIndex++; continue; }
			let start = m.index, end = m.index + m[0].length;
			// Drop a trailing comma or space the number pattern can swallow.
			while (end > start && /[\s,]/.test(s[end - 1])) end--;
			if (items.some((it) => start < it.end && end > it.start)) continue;
			let fields;
			try { fields = pat.read(m); } catch { fields = null; }
			if (!fields) continue;
			items.push({ kind: pat.kind, type: pat.type, loose: !!pat.loose, start, end, raw: s.slice(start, end).trim(), ...fields });
		}
	}
	return items.sort((a, b) => a.start - b.start);
}

// Numbers like "2.64" in "t(39) = 2.64" sit inside a stat item; numbers left outside every item, with a
// symbol and comparison in front, are pieces we couldn't read.
const UNREAD = /(?<![A-Za-z])([A-Za-zα-ωΑ-Ω][A-Za-z0-9α-ωΑ-Ω'²]{0,12}(?:\s?[(\[][^()[\]]{0,24}[)\]])?)\s?[=<>≤≥]\s?-?\s?\.?\d[\d.,]*/g;

// A statistic whose symbol was lost in copying (a PDF can drop χ, leaving "2 (9) = 10.57"): degrees of
// freedom and a value with nothing readable in front. Reported rather than silently skipped.
const UNREAD_NO_SYMBOL = /(?<![A-Za-z0-9)])(?:\d\s?)?[(\[]\s?\d+(?:\.\d+)?(?:\s?,\s?\d+(?:\.\d+)?)?\s?[)\]]\s?[=<>≤≥]\s?-?\s?\.?\d[\d.,]*/g;

function unreadPieces(s, items) {
	const covered = (a, b) => items.some((it) => a < it.end && b > it.start);
	const out = [];
	for (const re of [UNREAD, UNREAD_NO_SYMBOL]) {
		re.lastIndex = 0;
		let m;
		while ((m = re.exec(s))) {
			const a = m.index, b = m.index + m[0].length;
			if (covered(a, b) || out.some((o) => a < o.end && b > o.start)) continue;
			out.push({ start: a, end: b, text: m[0].replace(/[\s,;.]+$/, "") });
		}
	}
	return out.sort((x, y) => x.start - y.start);
}

const TOKEN_TEXT = { CHI2DF: "χ²/df", chi2: "χ²", G2: "G²", ETAP2: "η²p", ETAG2: "η²G", ETA2: "η²", OMEGAP2: "ω²p", OMEGA2: "ω²", EPS2: "ε²", CRAMERV: "V", PHI: "φ", DZ: "dz",
	F2ES: "f²", DR2: "ΔR²", ADJR2: "R²adj", R2: "R²", RS: "rₛ", TAU: "τ", PADJ: "p (adjusted)", BF10: "BF₁₀", BF01: "BF₀₁" };

// Canonical text back into something readable for display ("chi2(2, N = 90) = 7.1" → "χ²(2, N = 90) = 7.1").
export function displayText(s) {
	return String(s).replace(/\b(CHI2DF|chi2|G2|ETAP2|ETAG2|ETA2|OMEGAP2|OMEGA2|EPS2|CRAMERV|PHI|DZ|F2ES|DR2|ADJR2|R2|RS|TAU|PADJ|BF10|BF01)\b/g, (t) => TOKEN_TEXT[t] || t)
		.replace(/(χ²|G²) \(/g, "$1(").replace(/(^|[\s(=<>≤≥,[])-(?=\s?[.\d])/g, "$1−").replace(/\s+/g, " ").trim();
}

function statFrom(it) {
	const num = readNumber(it.num);
	if (!num) return null;
	const N = it.N ? readNumber(it.N)?.value : null;
	return { type: it.type, symbol: it.symbol, comp: COMP[it.comp] || "=", value: num.value, decimals: num.decimals, valueText: num.text,
		df1: it.df1 ?? null, df2: it.df2 ?? null, dfText: it.dfText ?? null, N: N ?? null, welch: !!it.welch, spearman: !!it.spearman };
}

function pFrom(it) {
	if (it.comp === "ns") return { comp: "ns", value: null, decimals: null, raw: it.raw };
	const num = readNumber(it.num);
	if (!num) return null;
	return { comp: COMP[it.comp] || "=", value: num.value, decimals: num.decimals, raw: it.raw, leadingZero: num.leadingZero, adjusted: !!it.adjusted };
}

function effectFrom(it) {
	const num = readNumber(it.num);
	if (!num) return null;
	const token = it.effect ?? (it.type === "r" ? (it.spearman ? "RS" : "r") : "OR");
	const meta = EFFECTS[token] || { key: token, symbol: token, term: null, name: token };
	return { ...meta, label: it.symbol && it.type === "OR" ? it.symbol : meta.symbol, comp: COMP[it.comp] || "=", value: num.value, decimals: num.decimals, raw: it.raw, ci: null };
}

// Model-fit indices: display label and glossary term (null where the glossary has none).
export const FITS = {
	CHI2DF: { key: "chi2df", label: "χ²/df", term: null, name: "normed chi-square" },
	CFI: { key: "cfi", label: "CFI", term: "cfi", name: "Comparative Fit Index" },
	TLI: { key: "tli", label: "TLI", term: null, name: "Tucker–Lewis Index" },
	NNFI: { key: "tli", label: "NNFI", term: null, name: "Non-Normed Fit Index (the Tucker–Lewis Index)" },
	RMSEA: { key: "rmsea", label: "RMSEA", term: "rmsea", name: "Root Mean Square Error of Approximation" },
	SRMR: { key: "srmr", label: "SRMR", term: "srmr", name: "Standardized Root Mean Square Residual" },
	GFI: { key: "gfi", label: "GFI", term: null, name: "Goodness-of-Fit Index" },
	AGFI: { key: "agfi", label: "AGFI", term: null, name: "Adjusted Goodness-of-Fit Index" },
	AIC: { key: "aic", label: "AIC", term: null, name: "Akaike Information Criterion" },
	BIC: { key: "bic", label: "BIC", term: null, name: "Bayesian Information Criterion" }
};

function fitFrom(it) {
	const num = readNumber(it.num);
	if (!num) return null;
	const meta = FITS[it.label];
	const lo = it.lo != null ? readNumber(it.lo) : null, hi = it.hi != null ? readNumber(it.hi) : null;
	const ci = lo && hi ? { level: it.level ? parseFloat(it.level) : null, lo: lo.value, hi: hi.value, loDecimals: lo.decimals, hiDecimals: hi.decimals } : null;
	return { ...meta, comp: COMP[it.comp] || "=", value: num.value, decimals: num.decimals, raw: it.raw, ci, start: it.start, end: it.end };
}

function ciFrom(it) {
	const lo = readNumber(it.lo), hi = readNumber(it.hi);
	if (!lo || !hi) return null;
	return { level: it.level, assumed: !!it.assumed, lo: lo.value, hi: hi.value, loDecimals: lo.decimals, hiDecimals: hi.decimals, raw: it.raw };
}

const ONE_TAILED = /one[\s-]?(?:tailed|sided)|directional/i;
const ADDS = ["U", "W"]; // statistics that are usually followed by their own z

// Parses a paste into results. Never throws.
export function parseResults(text) {
	const source = String(text ?? "");
	let s;
	try { s = normalize(source); } catch { s = source; }
	const empty = { text: s, results: [], unread: [], orphans: [], oneTailedInText: false };
	if (!s.trim()) return empty;
	try {
		const items = collectItems(s);
		const results = [];
		const orphans = [];
		let current = null;
		const pending = []; // coefficients and descriptives seen before the result they describe
		const pendingFits = []; // fit indices seen before their model's χ²
		let lastFit = null; // the fit index just read, which a following interval belongs to

		const startResult = (it, stat) => {
			const fits = pendingFits.splice(0);
			const start = Math.min(it.start, ...fits.map((f) => f.start));
			current = { id: results.length + 1, start, end: it.end, stat, p: null, extraP: [], effects: [], cis: [], fits, others: pending.splice(0), z: null, N: stat.N };
			results.push(current);
		};
		const nextAnchorIndex = (i) => items.findIndex((x, j) => j > i && x.kind === "stat" && !x.loose);
		items.forEach((it, i) => {
			const stretch = () => { if (current) current.end = Math.max(current.end, it.end); };
			if (it.kind !== "ci") lastFit = null;
			if (it.kind === "fit") {
				const fit = fitFrom(it);
				if (!fit) return;
				lastFit = fit;
				// A fit index belongs with its model's χ²: the open one, or the next result if that's a χ².
				const next = nextAnchorIndex(i);
				const nextIsChi = next !== -1 && items[next].type === "chi2";
				if (current && current.stat.type === "chi2") { current.fits.push(fit); stretch(); }
				else if (nextIsChi || !current) pendingFits.push(fit);
				else { current.fits.push(fit); stretch(); }
				return;
			}
			if (it.kind === "ci" && lastFit && !lastFit.ci) {
				const ci = ciFrom(it);
				if (ci) { lastFit.ci = ci; stretch(); }
				return;
			}
			if (it.kind === "stat") {
				const stat = statFrom(it);
				if (!stat) return;
				// V or W between 0 and 1 with decimals is Cramér's V, Kendall's W or Shapiro–Wilk's W, not a signed-rank sum.
				if (stat.type === "W" && Math.abs(stat.value) <= 1 && stat.decimals > 0) {
					if (current && (stat.symbol === "V" || current.stat.type === "chi2" || current.stat.type === "Q")) {
						const eff = effectFrom({ ...it, effect: stat.symbol === "V" ? "CRAMERV" : "KENDALLW" });
						if (eff) { current.effects.push(eff); stretch(); }
						return;
					}
					stat.type = "W01";
				}
				// A z right after U or W (before the p) is the normal approximation, part of that result.
				if (stat.type === "z" && current && ADDS.includes(current.stat.type) && !current.z && !current.p) { current.z = stat; stretch(); return; }
				if (it.loose) {
					// r or OR without df: its own result when nothing is open, or when the open result has its p
					// and this one has its own p before the next result starts.
					const next = nextAnchorIndex(i);
					const ownP = items.slice(i + 1, next === -1 ? items.length : next).some((x) => x.kind === "p");
					if (current && !(current.p && ownP)) {
						const eff = effectFrom(it);
						if (eff) { current.effects.push(eff); stretch(); }
						return;
					}
				}
				startResult(it, stat);
				return;
			}
			if (it.kind === "other") {
				const label = OTHER_LABELS[it.label] || it.label;
				const num = readNumber(it.num);
				const piece = { label: it.label, meaning: label, value: num?.value ?? null, raw: it.raw };
				const forward = ["b", "B", "β", "beta", "SE", "M", "SD", "Mdn"].includes(it.label);
				if (current && !(forward && current.p)) { current.others.push(piece); stretch(); }
				else pending.push(piece);
				return;
			}
			if (!current) {
				orphans.push({ kind: it.kind, raw: it.raw });
				return;
			}
			if (it.kind === "p") {
				const p = pFrom(it);
				if (!p) return;
				if (current.p) current.extraP.push(p); else current.p = p;
			} else if (it.kind === "effect") {
				const eff = effectFrom(it);
				if (eff) current.effects.push(eff);
			} else if (it.kind === "ci") {
				const ci = ciFrom(it);
				if (!ci) return;
				// A CI belongs to the effect size just before it, if it doesn't have one yet; otherwise to the result.
				const last = current.effects[current.effects.length - 1];
				if (last && !last.ci && it.start >= current.start) last.ci = ci; else current.cis.push(ci);
			}
			stretch();
		});
		// Fit indices with no χ² at all make a result of their own.
		if (pendingFits.length && !results.length) {
			const fits = pendingFits.splice(0);
			results.push({ id: 1, start: Math.min(...fits.map((f) => f.start)), end: Math.max(...fits.map((f) => f.end)), stat: { type: "fit", comp: "=", value: null, decimals: 0, df1: null, df2: null, N: null },
				p: null, extraP: [], effects: [], cis: [], fits, others: pending.splice(0), z: null, N: null });
		}
		if (pendingFits.length && results.length) {
			const last = results[results.length - 1];
			last.fits.push(...pendingFits.splice(0));
			last.end = Math.max(last.end, ...last.fits.map((f) => f.end));
		}
		if (pending.length && results.length) results[results.length - 1].others.push(...pending.splice(0));
		for (const r of results) {
			r.raw = displayText(s.slice(r.start, r.end));
			const nPiece = r.others.find((o) => o.label === "N" && o.value);
			if (!r.N && nPiece) r.N = nPiece.value;
			const segmentEnd = results[r.id] ? results[r.id].start : s.length;
			// The words around a result: its own sentence back to the previous result, and up to the next one.
			const prevEnd = r.id > 1 ? results[r.id - 2].end : 0;
			let from = Math.max(prevEnd, r.start - 160);
			const before = s.slice(from, r.start);
			const cut = Math.max(before.search(/[.;]\s+(?=[A-Z])[^.;]*$/), before.lastIndexOf(";"));
			if (cut >= 0) from += cut + 1;
			const after = s.slice(r.end, segmentEnd);
			const stop = after.search(/[.;]\s+(?=[A-Z])|;/);
			r.context = s.slice(from, stop >= 0 ? r.end + stop : segmentEnd);
			r.oneTailed = ONE_TAILED.test(s.slice(r.start, segmentEnd));
		}
		const unread = unreadPieces(s, items).map((u) => ({ text: displayText(u.text) }));
		return { text: s, results, unread, orphans: orphans.map((o) => ({ ...o, raw: displayText(o.raw) })), oneTailedInText: ONE_TAILED.test(s) };
	} catch (e) {
		return { ...empty, unread: [{ text: displayText(s.slice(0, 200)) }], error: String(e && e.message) };
	}
}
